<template>
  <div class="nested-draggable" :class="{ 'is-readonly': attrs.readonly, 'is-minimal': attrs.minimal }">
    <draggable v-if="canRender" class="drag-area" :class="{ 'is-empty': !localChildren.length && !attrs.readonly }"
      tag="ul" :list="localChildren" :group="{ name: 'g1' }" :empty-insert-threshold="50" :force-fallback="true"
      :disabled="attrs.readonly" @change="onDragChange" @end="onDragEnd">
      <li v-for="(node, index) in localChildren" :key="getNodeKey(node, index)" class="drag-item">
        <!-- Resolved Card -->
        <template v-if="getCard(node)">
          <v-row>
            <!-- Color Indicator -->
            <v-col v-if="getCard(node).color" cols="1">
              <div class="color-circle" :style="{ backgroundColor: getCard(node).color }">
                {{ getCard(node).label }}
              </div>
            </v-col>

            <!-- Card Content -->
            <v-col :cols="getCard(node).color ? 11 : 12">
              <h2 :class="textSizeClass" class="mb-0">
                {{ getCardName(getCard(node)) }}
                <span v-if="getCard(node).spotlight">*</span>
              </h2>

              <p v-if="!hideChildren && !attrs.minimal && getCard(node).sentence"
                class="text-styled-h4 font-weight-regular mb-0 mt-2">
                {{ getCard(node).sentence }}
              </p>
            </v-col>

            <!-- Tags Section -->
            <v-col v-if="attrs.tags && !attrs.minimal" cols="12">
              <article v-if="hasItems(getCard(node).categories)">
                <p class="text-caption text-uppercase mb-2">Categories</p>
                <div class="d-flex flex-wrap">
                  <v-chip v-for="(tag, i) in getCard(node).categories" :key="`cat-${node.id}-${depth}-${i}`" small light
                    class="mr-1 mb-1 yellow lighten-3">
                    {{ resolveTitle('categories', tag) }}
                  </v-chip>
                </div>
              </article>

              <article v-if="hasItems(getCard(node).assets)" class="mt-2">
                <p class="text-caption text-uppercase mb-2">Assets / Brands</p>
                <div class="d-flex flex-wrap">
                  <v-chip v-for="(tag, i) in getCard(node).assets" :key="`asset-${node.id}-${depth}-${i}`" small light
                    class="mr-1 mb-1 green lighten-4">
                    {{ resolveTitle('assets', tag) }}
                  </v-chip>
                </div>
              </article>

              <article v-if="hasItems(getCard(node).tags)" class="mt-2">
                <p class="text-caption text-uppercase mb-2">Tags</p>
                <div class="d-flex flex-wrap">
                  <v-chip v-for="(tag, i) in getCard(node).tags" :key="`tag-${node.id}-${depth}-${i}`" small light
                    class="mr-1 mb-1 yellow lighten-5">
                    {{ resolveTitle('tags', tag) }}
                  </v-chip>
                </div>
              </article>

              <article v-if="hasItems(getCard(node).audiences)" class="mt-2">
                <p class="text-caption text-uppercase mb-2">Audiences</p>
                <div class="d-flex flex-wrap">
                  <v-chip v-for="(tag, i) in getCard(node).audiences" :key="`aud-${node.id}-${depth}-${i}`" small light
                    class="mr-1 mb-1 yellow lighten-3">
                    {{ resolveTitle('audiences', tag) }}
                  </v-chip>
                </div>
              </article>

              <article v-if="hasItems(getCard(node).features)" class="mt-2">
                <p class="text-caption text-uppercase mb-2">Features</p>
                <div class="d-flex flex-wrap">
                  <v-chip v-for="(tag, i) in getCard(node).features" :key="`feat-${node.id}-${depth}-${i}`" small light
                    class="mr-1 mb-1 yellow lighten-3">
                    {{ resolveTitle('features', tag) }}
                  </v-chip>
                </div>
              </article>

              <article v-if="hasItems(getCard(node).benefits)" class="mt-2">
                <p class="text-caption text-uppercase mb-2">Benefits</p>
                <div class="d-flex flex-wrap">
                  <v-chip v-for="(tag, i) in getCard(node).benefits" :key="`ben-${node.id}-${depth}-${i}`" small light
                    class="mr-1 mb-1 yellow lighten-3">
                    {{ resolveTitle('benefits', tag) }}
                  </v-chip>
                </div>
              </article>
            </v-col>

            <!-- Description -->
            <v-col v-if="attrs.note && !attrs.minimal && getCard(node).body" cols="12">
              <p class="text-caption text-uppercase mb-2">Description</p>
              <div class="pre-wrap" v-html="sanitizeHtml(getCard(node).body)" />
            </v-col>

            <!-- Historical Narrative -->
            <v-col v-if="attrs.historic && !attrs.minimal && getCard(node).historical" cols="12">
              <p class="text-caption text-uppercase mb-2">Historical Narrative</p>
              <div class="pre-wrap" v-html="sanitizeHtml(getCard(node).historical)" />
            </v-col>

            <!-- Graph Section  -->
            <v-col v-if="attrs.graph && !attrs.minimal && getCard(node) && hasNonZeroScores(getCard(node).scores)"
              cols="12">
              <BarRadar :data="getCard(node).scores"
                :id="`radar-${node.id}-${depth}${attrs.readonly ? '-view' : ''}`" />
            </v-col>

            <!-- Stats section -->
            <v-col v-if="attrs.stats && !attrs.minimal && getCard(node) && getCard(node).scores" cols="12">
              <div v-for="score in scoreFields" :key="score.key">
                <p class="ma-0 pa-0">{{ score.label }}</p>
                <v-slider v-model="getCard(node).scores[score.key]" :min="0" :max="10" step="1" ticks="always"
                  tick-size="4" color="amber" track-color="grey lighten-2" class="ma-0 pa-0" hide-details
                  :readonly="true" />
              </div>
            </v-col>

            <!-- Actions -->
            <v-col v-if="attrs.controls && !attrs.minimal" cols="12">
              <div class="d-flex justify-end align-center flex-wrap pr-4">
                <v-btn x-small color="white" light @click="handleEdit(node.id)">
                  Edit
                </v-btn>
                <v-btn x-small color="white" light class="ml-2" @click="handleClone(node.id)">
                  Clone
                </v-btn>
                <v-btn x-small color="white" light class="ml-2" @click="confirmDelete(node.id)">
                  Delete
                </v-btn>
              </div>
            </v-col>
          </v-row>

          <!-- Recursive Children (with depth protection) -->
          <div v-if="canShowChildrenArea(node)" class="nested-children"
            :class="{ 'has-children': hasNodeChildren(node) }">
            <nested-draggable-tabs :children="getNodeChildren(node)" :cards-by-id="cardsById" :attrs="attrs"
              :local="local" :depth="nextDepth" :hide-children="hideChildren" :visited-ids="childVisitedIds(node)"
              @editCard="$emit('editCard', $event)" @deleteCard="$emit('deleteCard', $event)"
              @cloneCard="$emit('cloneCard', $event)" @cardMoved="$emit('cardMoved', $event)"
              @update:children="updateNodeChildren(node, $event)" />
          </div>
        </template>

        <!-- Missing Card Fallback -->
        <template v-else-if="node && node.id">
          <v-row>
            <v-col cols="12">
              <v-alert type="warning" dense text>
                Missing card: {{ node.id }}
              </v-alert>
            </v-col>
          </v-row>
        </template>

        <!-- Invalid Node -->
        <template v-else>
          <v-row>
            <v-col cols="12">
              <v-alert type="error" dense text>
                Invalid node at index {{ index }}
              </v-alert>
            </v-col>
          </v-row>
        </template>
      </li>
    </draggable>

    <!-- Depth Limit Warning -->
    <v-alert v-else-if="isDepthExceeded" type="info" dense text class="ma-2">
      Maximum nesting depth reached
    </v-alert>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">
          Delete this item?
        </v-card-title>
        <v-card-text>
          This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="grey" text @click="cancelDelete">
            Cancel
          </v-btn>
          <v-btn color="red" text @click="executeDelete">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { BarRadar } from '@/components/graphs'
import { scoreFields } from './model'

// Maximum allowed nesting depth to prevent infinite recursion
const MAX_DEPTH = 10

export default {
  name: 'NestedDraggableTabs',

  components: {
    draggable, BarRadar
  },

  props: {
    // Array of node objects: [{ id: string, children: [] }, ...]
    children: {
      type: Array,
      required: true,
      default: () => [],
    },

    // Card lookup map
    cardsById: {
      type: Object,
      default: () => ({}),
    },

    // Display attributes
    attrs: {
      type: Object,
      default: () => ({}),
    },

    // Current nesting depth
    depth: {
      type: Number,
      default: 0,
    },

    // Local lookup data (categories, assets, tags, audiences with {id, title})
    local: {
      type: Object,
      default: () => ({}),
    },

    // Whether to hide nested children
    hideChildren: {
      type: Boolean,
      default: false,
    },

    // Array of visited node IDs (for circular reference detection)
    // Using Array instead of Set for Vue 2 prop validation compatibility
    visitedIds: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      localChildren: this.cloneChildren(this.children),
      showDeleteDialog: false,
      deleteTargetId: null,
      scoreFields: scoreFields
    }
  },

  computed: {
    nextDepth() {
      return this.depth + 1
    },

    isDepthExceeded() {
      return this.depth >= MAX_DEPTH
    },

    canRender() {
      return !this.isDepthExceeded && Array.isArray(this.localChildren)
    },

    textSizeClass() {
      const type = this.attrs.type

      if (type === 'large') return 'text-h5'
      if (type === 'medium') return 'text-h6'
      if (type === 'small') return 'text-body-1'

      if (type === 'waterfall') {
        const sizes = ['text-h5', 'text-h6', 'text-body-1', 'text-body-2']
        return sizes[Math.min(this.depth, sizes.length - 1)]
      }

      return 'text-h5'
    },

    // Convert visitedIds array to Set for O(1) lookup
    visitedIdsSet() {
      return new Set(this.visitedIds)
    },
  },

  watch: {
    children: {
      handler(newVal) {
        // Only update if actually different
        const newStr = JSON.stringify(newVal)
        const localStr = JSON.stringify(this.localChildren)
        if (newStr !== localStr) {
          this.localChildren = this.cloneChildren(newVal)
        }
      },
      deep: true,
    },

    localChildren: {
      handler(newVal) {
        // Emit changes to parent
        const newStr = JSON.stringify(newVal)
        const propStr = JSON.stringify(this.children)
        if (newStr !== propStr) {
          this.$emit('update:children', newVal)
        }
      },
      deep: true,
    },
  },

  methods: {
    // ─────────────────────────────────────────────────────────────────
    // Data Helpers
    // ─────────────────────────────────────────────────────────────────

    cloneChildren(arr) {
      if (!Array.isArray(arr)) return []
      try {
        const cloned = JSON.parse(JSON.stringify(arr))
        // Ensure all nodes have children arrays
        const ensureChildren = (nodes) => {
          for (const node of nodes) {
            if (node && typeof node === 'object') {
              if (!Array.isArray(node.children)) {
                node.children = []
              } else {
                ensureChildren(node.children)
              }
            }
          }
        }
        ensureChildren(cloned)
        return cloned
      } catch (e) {
        console.error('[NestedDraggable] Failed to clone children:', e)
        return []
      }
    },

    getNodeKey(node, index) {
      if (node?.id) {
        return `node-${node.id}-${this.depth}-${index}`
      }
      return `node-invalid-${this.depth}-${index}`
    },

    getCard(node) {
      if (!node?.id) return null
      return this.cardsById[node.id] || null
    },

    getCardName(card) {
      if (!card) return ''

      const name = card.name || card.title || 'Untitled'

      // Handle prefix logic if configured
      if (this.attrs.prefix && card.allowPrefix) {
        switch (this.attrs.prefixPosition) {
          case 'after':
            return `${name}${this.attrs.prefix}`
          case 'after-space':
            return `${name} ${this.attrs.prefix}`
          case 'before':
            return `${this.attrs.prefix}${name}`
          case 'before-space':
            return `${this.attrs.prefix} ${name}`
        }
      }

      return name
    },

    hasItems(arr) {
      return Array.isArray(arr) && arr.length > 0
    },

    resolveTitle(type, id) {
      const items = this.local?.[type]
      if (!Array.isArray(items)) return id
      const found = items.find((item) => item.id === id)
      return found?.title || id
    },

    hasNonZeroScores(scores) {
      if (!scores || typeof scores !== 'object') return false
      return Object.values(scores).some((val) => val > 0)
    },

    sanitizeHtml(html) {
      // Basic XSS protection - in production, use a proper sanitizer like DOMPurify
      if (!html) return ''
      return String(html)
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/on\w+="[^"]*"/gi, '')
        .replace(/on\w+='[^']*'/gi, '')
    },

    // ─────────────────────────────────────────────────────────────────
    // Circular Reference Protection
    // ─────────────────────────────────────────────────────────────────

    /**
     * Check if we should show the children drop area (even if empty)
     */
    canShowChildrenArea(node) {
      if (this.hideChildren) return false
      if (!this.attrs.children) return false // Don't show nested children unless enabled
      if (this.nextDepth >= MAX_DEPTH) return false

      // Check for circular reference using the computed Set
      if (this.visitedIdsSet.has(node.id)) {
        console.warn(`[NestedDraggable] Circular reference detected for node: ${node.id}`)
        return false
      }

      // In readonly mode, only show children area if there are actual children
      if (this.attrs.readonly && !this.hasNodeChildren(node)) {
        return false
      }

      return true
    },

    hasNodeChildren(node) {
      return node?.children?.length > 0
    },

    getNodeChildren(node) {
      // Ensure we always return an array, even if empty
      if (!node) return []
      if (!Array.isArray(node.children)) {
        // Initialize children array if it doesn't exist
        this.$set(node, 'children', [])
      }
      return node.children
    },

    childVisitedIds(node) {
      // Return array (not Set) for Vue 2 prop compatibility
      const newArr = [...this.visitedIds]
      if (node?.id) {
        newArr.push(node.id)
      }
      return newArr
    },

    // ─────────────────────────────────────────────────────────────────
    // Event Handlers
    // ─────────────────────────────────────────────────────────────────

    onDragChange(evt) {
      // Draggable already mutates localChildren, which triggers the watcher

      // Ensure added elements always have a children array for nesting support
      if (evt.added?.element) {
        const addedElement = evt.added.element
        if (!Array.isArray(addedElement.children)) {
          this.$set(addedElement, 'children', [])
        }
        // Emit cardMoved event when a card is added to this list
        if (addedElement.id) {
          this.$emit('cardMoved', addedElement.id)
        }
      }

      // Also ensure all items in localChildren have children arrays
      for (const node of this.localChildren) {
        if (node && !Array.isArray(node.children)) {
          this.$set(node, 'children', [])
        }
      }
    },

    onDragEnd() {
      // Bubble up that a drag operation completed
      this.$emit('dragEnd')
    },

    updateNodeChildren(node, newChildren) {
      const idx = this.localChildren.findIndex((n) => n?.id === node?.id)
      if (idx !== -1) {
        this.$set(this.localChildren[idx], 'children', newChildren)
      }
    },

    handleEdit(cardId) {
      this.$emit('editCard', cardId)
    },

    handleClone(cardId) {
      this.$emit('cloneCard', cardId)
    },

    confirmDelete(cardId) {
      this.deleteTargetId = cardId
      this.showDeleteDialog = true
    },

    cancelDelete() {
      this.showDeleteDialog = false
      this.deleteTargetId = null
    },

    executeDelete() {
      if (this.deleteTargetId) {
        this.$emit('deleteCard', this.deleteTargetId)
      }
      this.cancelDelete()
    },
  },
}
</script>

<style lang="scss" scoped>
.nested-draggable {

  user-select: none;

  // Minimal mode - compact cards showing only titles
  &.is-minimal {
    .drag-item {
      padding: 0.25rem 0.5rem;
      margin-bottom: 2px;
    }

    .nested-children {
      margin: 2px 0 2px 12px;
      padding: 0;
      min-height: 16px;
      background: transparent;
      border: none;

      .drag-area {
        min-height: 12px !important;
      }

      &:not(.has-children) {
        background: rgba(0, 0, 0, 0.01);
        min-height: 12px;
      }

      &.has-children {
        background: transparent;
      }

      &:hover {
        background: rgba(255, 193, 7, 0.03);
      }
    }
  }

  // Readonly mode - no drag styling
  &.is-readonly {
    .drag-area {
      min-height: auto;
      padding: 0;

      &.is-empty {
        min-height: auto;
        background: transparent;
        border: none;

        &::after {
          content: none;
        }
      }
    }

    .drag-item {
      cursor: default;

      &:hover {
        box-shadow: none;
      }

      &:active {
        cursor: default;
      }
    }

    .nested-children {
      margin: 4px 0 4px 12px;
      padding: 0;
      min-height: auto;
      background: transparent;
      border: none;

      &::before {
        content: none;
      }

      &:hover {
        background: transparent;
        box-shadow: none;
      }

      &.has-children {
        background: transparent;
      }
    }
  }

  .drag-area {
    min-height: 40px;
    padding: 4px;
    margin: 0;
    border-radius: 4px;
    list-style: none;

    // Empty state for top-level drag area
    &.is-empty {
      min-height: 80px;
      background: #f5f5f5;
      border: 2px dashed #bdbdbd;
      display: flex;
      align-items: center;
      justify-content: center;

      &::after {
        content: 'Drop items here';
        color: #9e9e9e;
        font-size: 14px;
        font-weight: 500;
      }
    }

    // Vuedraggable ghost styling
    .sortable-ghost {
      opacity: 0.4;
      background: #ffc107 !important;
      border: 2px dashed #ff9800 !important;
    }

    .sortable-chosen {
      background: #fff8e1 !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .drag-item {
      list-style: none;
      background: rgb(245, 245, 245);
      padding: 0.5rem 0.75rem;
      cursor: grab;
      border: none;
      border-radius: 0;
      margin-bottom: 4px;
      transition: all 0.2s ease;

      &:hover {
        background: rgb(250, 250, 250);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }

      &:active {
        cursor: grabbing;
      }

      // Nested depth coloring
      .drag-area .drag-item {
        background: rgb(235, 235, 235);

        .drag-area .drag-item {
          background: rgb(225, 225, 225);

          .drag-area .drag-item {
            background: rgb(215, 215, 215);
          }
        }
      }
    }
  }

  // Nested children drop zone
  .nested-children {
    margin: 4px 0 4px 16px;
    padding: 0;
    min-height: 30px;
    background: transparent;
    border: none;
    border-radius: 0;
    transition: all 0.2s ease;
    position: relative;

    // Ensure the draggable inside fills the container
    .nested-draggable {
      min-height: 100%;
      width: 100%;
    }

    .drag-area {
      min-height: 24px !important;
      background: transparent;
    }

    // Empty state - subtle indicator
    &:not(.has-children) {
      background: rgba(0, 0, 0, 0.02);
      min-height: 20px;

      &::before {
        content: none;
      }
    }

    // With children
    &.has-children {
      background: transparent;
    }

    // Hover state
    &:hover {
      background: rgba(255, 193, 7, 0.03);
    }
  }

  .color-circle {
    width: 30px;
    height: 30px;
    background: grey;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 12px;
    font-weight: bold;
  }

  .pre-wrap {
    white-space: pre-wrap;
    word-break: break-word;
  }
}
</style>